<template>
  <v-dialog
    :value="value"
    scrollable
    max-width="900"
    @input="$emit('input', $event)"
  >
    <v-card v-if="record" rounded="0">
      <v-toolbar color="kenyaGreen" dark flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.greenCoffee.bulkImport.detailsTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="px-6 py-5" style="max-height: 72vh">
        <div class="d-flex align-center mb-4">
          <h3 class="font-weight-bold black--text">
            {{ $t('memberDataSection.greenCoffee.bulkImport.detailsTitle') }}
          </h3>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="white--text" @click="$emit('edit', record)">
            {{ $t('edit') }}
          </v-btn>
        </div>

        <!-- Issue alert -->
        <div
          v-if="hasIssues"
          class="bulk-detail-alert mb-5"
          :style="{
            backgroundColor: themeColor('secondary2light'),
            borderColor: themeColor('third'),
          }"
        >
          <p class="font-weight-bold black--text mb-1">{{ issueTitle }}</p>
          <p class="text-body-2 mb-0">{{ issueDescription }}</p>
        </div>

        <!-- Header fields -->
        <v-row dense>
          <v-col
            v-for="field in headerFields"
            :key="field.key"
            cols="12"
            md="6"
            class="mb-2"
          >
            <div class="text-body-2 text--secondary mb-1">{{ $t(field.labelKey) }}</div>
            <div
              class="text-body-1 font-weight-bold"
              :class="field.error ? 'red--text' : 'black--text'"
            >
              {{ field.value }}
            </div>
          </v-col>
        </v-row>

        <!-- Quality control (one card per milling-output lot) -->
        <div
          v-for="(lot, index) in gradings"
          :key="index"
          class="bulk-detail-lot mt-4"
          :style="{ backgroundColor: themeColor('themeGrey') }"
        >
          <v-row dense>
            <v-col
              v-for="field in gradingFields"
              :key="field.key"
              cols="12"
              md="6"
              class="mb-2"
            >
              <div class="text-body-2 text--secondary mb-1">{{ $t(field.labelKey) }}</div>
              <div class="text-body-1 font-weight-bold black--text">
                {{ displayValue(lot[field.key]) }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4 px-6">
        <v-btn outlined color="primary" class="mr-3" min-width="120" @click="$emit('edit', record)">
          {{ $t('edit') }}
        </v-btn>
        <v-btn color="primary" class="white--text" min-width="120" @click="$emit('input', false)">
          {{ $t('memberDataSection.greenCoffee.bulkImport.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const B = "memberDataSection.greenCoffee.bulkImport";

export default {
  name: "GreenCoffeeBulkImportDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    issueTitle: { type: String, default: "" },
    issueDescription: { type: String, default: "" },
  },
  data() {
    return {
      gradingFields: [
        { key: "cleanCoffee", labelKey: `${B}.millingCleanCoffee` },
        { key: "bags", labelKey: `${B}.millingBags` },
        { key: "pockets", labelKey: `${B}.millingPockets` },
        { key: "weight", labelKey: `${B}.millingWeight` },
        { key: "bulkOutturn", labelKey: `${B}.millingBulkOutturn` },
        { key: "ratio", labelKey: `${B}.millingRatio` },
      ],
    };
  },
  computed: {
    rec() {
      return (this.record && this.record.recordedJsonData) || {};
    },
    errorMetadata() {
      return (this.record && this.record.errorMetadata) || {};
    },
    hasIssues() {
      return Boolean(this.record && (this.record.errorCodes || []).length);
    },
    gradings() {
      return this.rec.qualityGradings || [];
    },
    headerFields() {
      const md = this.errorMetadata;
      return [
        { key: "inputLotId", labelKey: `${B}.editFieldId`, value: this.displayValue(this.rec.inputLotId) },
        {
          key: "outturnNumber",
          labelKey: `${B}.outturnNo`,
          value: this.displayValue(this.rec.outturnNumber),
          error: Boolean(md.outturnNumber && md.outturnNumber.length),
        },
        {
          key: "growerCode",
          labelKey: `${B}.growerCode`,
          value: this.displayValue(this.rec.growerCode),
          error: Boolean(md.growerCode && md.growerCode.length),
        },
        {
          key: "greenBeansTotal",
          labelKey: `${B}.greenBeansTotal`,
          value: this.displayValue(this.rec.greenBeansTotal),
          error: Boolean(md.greenBeansTotal && md.greenBeansTotal.length),
        },
        {
          key: "growerName",
          labelKey: `${B}.growerName`,
          value: this.displayValue(this.rec.growerName),
          error: Boolean(md.growerName && md.growerName.length),
        },
        { key: "coffeeType", labelKey: `${B}.coffeeType`, value: this.displayValue(this.rec.coffeeType) },
      ];
    },
  },
  methods: {
    displayValue(v) {
      return v != null && String(v).trim() !== "" ? v : "—";
    },
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-detail-alert {
  border: 1px solid;
  border-radius: 8px;
  padding: 16px 20px;
}

.bulk-detail-lot {
  border-radius: 8px;
  padding: 16px 20px;
}
</style>
